import React from 'react';
import { ArrowLeft, Globe, FileText } from 'lucide-react';

interface TermsOfUseProps {
  onBack: () => void;
}

const TermsOfUse: React.FC<TermsOfUseProps> = ({ onBack }) => {
  return (
    <div className="min-h-screen bg-white dark:bg-navy-950 font-sans text-slate-700 dark:text-slate-300">
      {/* Header */}
      <nav className="fixed top-0 w-full z-[100] bg-white/90 dark:bg-navy-950/90 backdrop-blur-md border-b border-neutral-100 dark:border-navy-800 px-6 py-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <button 
            onClick={onBack}
            className="flex items-center gap-2 text-sm font-bold text-slate-500 hover:text-navy-950 dark:hover:text-white transition-colors uppercase tracking-widest"
          >
            <ArrowLeft size={16} /> Voltar
          </button>
          <div className="flex items-center gap-3">
            <div className="bg-navy-900 dark:bg-parliament-600 p-1.5 rounded-lg text-white">
              <Globe size={16} />
            </div>
            <span className="font-black text-sm tracking-tighter uppercase dark:text-white">Gabinete Digital</span>
          </div>
        </div>
      </nav>

      <main className="pt-32 pb-20 px-6">
        <article className="max-w-4xl mx-auto prose dark:prose-invert prose-slate prose-headings:font-black prose-headings:uppercase prose-headings:tracking-tighter">
          
          <div className="mb-12 text-center">
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-parliament-50 dark:bg-parliament-900/20 text-parliament-600 dark:text-parliament-400 text-[10px] font-black uppercase tracking-widest mb-4 border border-parliament-100 dark:border-parliament-800">
              <FileText size={14} /> Contrato de Serviço
            </span>
            <h1 className="text-4xl md:text-5xl mb-2 text-navy-950 dark:text-white">Termos de Uso</h1>
            <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">Última atualização: 01/02/2026</p>
          </div>

          <div className="bg-neutral-50 dark:bg-navy-900/50 p-8 rounded-[32px] border border-neutral-200 dark:border-navy-800 space-y-8">
            <section>
              <h3>1. IDENTIFICAÇÃO</h3>
              <p>Estes Termos de Uso regulam o acesso e utilização da plataforma <strong>Gabinete Digital</strong>, operada por:</p>
              <p><strong>ALMIRANTE DIGITAL LTDA</strong><br/>
              CNPJ: 59.120.082/0001-50<br/>
              Sede: Governador Valadares/MG<br/>
              E-mail: agencia@dialetike.org</p>
              <p>Administrador responsável: Abel Henrique da Silva</p>
              <p>Ao contratar ou utilizar a plataforma, o usuário declara que leu, compreendeu e concorda integralmente com estes Termos.</p>
            </section>

            <section>
              <h3>2. OBJETO</h3>
              <p>A plataforma Gabinete Digital consiste em um sistema SaaS destinado à:</p>
              <ul>
                <li>Gestão de relacionamento parlamentar (CRM);</li>
                <li>Organização de demandas e atendimentos;</li>
                <li>Registro de documentos e comunicações institucionais;</li>
                <li>Segmentação de contatos;</li>
                <li>Controle interno de equipe.</li>
              </ul>
              <p>A ALMIRANTE DIGITAL disponibiliza exclusivamente infraestrutura tecnológica.</p>
            </section>

            <section>
              <h3>3. MODELO DE CONTRATAÇÃO</h3>
              <p>A plataforma é disponibilizada no modelo de assinatura mensal (SaaS).</p>
              <p>O acesso é condicionado:</p>
              <ul>
                <li>à contratação ativa;</li>
                <li>ao pagamento regular via gateway Asaas;</li>
                <li>ao aceite destes Termos e da Política de Privacidade.</li>
              </ul>
              <p>A inadimplência poderá resultar em suspensão de acesso, limitação de funcionalidades ou cancelamento da conta após prazo de tolerância.</p>
            </section>

            <section>
              <h3>4. ELEGIBILIDADE</h3>
              <p>A plataforma destina-se a:</p>
              <ul>
                <li>Gabinetes parlamentares;</li>
                <li>Agentes políticos;</li>
                <li>Assessores;</li>
                <li>Candidatos;</li>
                <li>Chefias de gabinete de prefeituras ou órgãos públicos;</li>
                <li>Outras organizações que desejem utilizar o sistema para gestão institucional.</li>
              </ul>
              <p>O contratante declara possuir capacidade legal para firmar o contrato.</p>
            </section>

            <section>
              <h3>5. RESPONSABILIDADE SOBRE OS DADOS</h3>
              <p>O Gabinete contratante é o <strong>Controlador dos dados pessoais inseridos na plataforma</strong>.</p>
              <p>A ALMIRANTE DIGITAL atua exclusivamente como <strong>Operadora</strong>, limitando-se a armazenar e manter a infraestrutura do sistema.</p>
              <p>O contratante é integralmente responsável por:</p>
              <ul>
                <li>coletar dados de forma lícita;</li>
                <li>possuir base legal adequada;</li>
                <li>obter consentimento quando necessário;</li>
                <li>garantir a legitimidade da manutenção de dados eleitorais ou políticos;</li>
                <li>responder a solicitações de titulares;</li>
                <li>cumprir obrigações da LGPD.</li>
              </ul>
              <p>A plataforma não realiza tratamento autônomo, comercialização ou uso próprio dos dados inseridos.</p>
            </section>

            <section>
              <h3>6. CONTA E ACESSO</h3>
              <p>O administrador do gabinete é responsável por:</p>
              <ul>
                <li>criar e gerenciar usuários internos;</li>
                <li>definir níveis de permissão;</li>
                <li>manter a confidencialidade das credenciais;</li>
                <li>revogar acessos quando necessário.</li>
              </ul>
              <p>A utilização indevida por usuário interno é de responsabilidade do contratante.</p>
            </section>

            <section>
              <h3>7. PROIBIÇÕES</h3>
              <p>É vedado utilizar a plataforma para:</p>
              <ul>
                <li>armazenar dados obtidos de forma ilícita;</li>
                <li>praticar assédio, fraude ou ilícitos eleitorais;</li>
                <li>violar direitos de terceiros;</li>
                <li>disseminar spam ou mensagens não autorizadas;</li>
                <li>inserir conteúdos que violem legislação vigente.</li>
              </ul>
              <p>A violação poderá resultar em suspensão ou cancelamento imediato da conta.</p>
            </section>

            <section>
              <h3>8. DISPONIBILIDADE DO SERVIÇO</h3>
              <p>A ALMIRANTE DIGITAL envidará esforços para manter o sistema disponível, mas não garante disponibilidade ininterrupta.</p>
              <p>Poderão ocorrer manutenções programadas, instabilidades técnicas ou indisponibilidade decorrente de provedores terceiros (Hostinger, Supabase, Asaas).</p>
              <p>Não há garantia de nível mínimo de serviço (SLA), salvo previsão contratual específica.</p>
            </section>

            <section>
              <h3>9. SERVIÇOS DE TERCEIROS</h3>
              <p>A plataforma utiliza serviços de terceiros, incluindo:</p>
              <ul>
                <li>Hostinger (hospedagem);</li>
                <li>Supabase (banco de dados e autenticação);</li>
                <li>Supabase Storage (armazenamento);</li>
                <li>Asaas (processamento de pagamentos);</li>
                <li>Google Maps (geolocalização).</li>
              </ul>
              <p>A ALMIRANTE DIGITAL não se responsabiliza por falhas diretamente atribuíveis a esses provedores.</p>
            </section>

            <section>
              <h3>10. PROPRIEDADE INTELECTUAL</h3>
              <p>O software, código-fonte, layout, marca e funcionalidades são de titularidade exclusiva da ALMIRANTE DIGITAL LTDA.</p>
              <p>É proibido copiar, modificar, realizar engenharia reversa, sublicenciar ou explorar comercialmente o sistema sem autorização expressa.</p>
            </section>

            <section>
              <h3>11. CANCELAMENTO E ENCERRAMENTO</h3>
              <p>O contratante poderá solicitar cancelamento a qualquer momento.</p>
              <p>Após o encerramento, o acesso será bloqueado, os dados poderão ser exportados mediante solicitação e, após prazo razoável, poderão ser excluídos.</p>
              <p>A empresa poderá rescindir unilateralmente em caso de violação destes Termos.</p>
            </section>

            <section>
              <h3>12. LIMITAÇÃO DE RESPONSABILIDADE</h3>
              <p>A ALMIRANTE DIGITAL não se responsabiliza por:</p>
              <ul>
                <li>uso indevido da plataforma pelo contratante;</li>
                <li>decisões políticas tomadas com base nos dados;</li>
                <li>ilegalidade na coleta de dados pelo gabinete;</li>
                <li>conteúdo inserido pelos usuários;</li>
                <li>danos indiretos, lucros cessantes ou prejuízos eleitorais.</li>
              </ul>
              <p>A responsabilidade da empresa limita-se ao valor pago nos últimos 12 meses de contrato.</p>
            </section>

            <section>
              <h3>13. PROTEÇÃO DE DADOS</h3>
              <p>O tratamento de dados pessoais é regido pela Política de Privacidade da plataforma. Em caso de conflito entre este documento e a Política de Privacidade, prevalecerá a norma específica de proteção de dados.</p>
            </section>

            <section>
              <h3>14. ALTERAÇÕES</h3>
              <p>A ALMIRANTE DIGITAL poderá modificar estes Termos a qualquer tempo. A continuidade de uso após atualização implicará concordância com a nova versão.</p>
            </section>

            <section>
              <h3>15. FORO E LEGISLAÇÃO APLICÁVEL</h3>
              <p>Estes Termos de Uso serão regidos e interpretados de acordo com a legislação da República Federativa do Brasil.</p>
              <p>Fica eleito o foro da Comarca de São Paulo/SP para dirimir quaisquer controvérsias oriundas destes Termos, com renúncia expressa a qualquer outro, por mais privilegiado que seja.</p>
            </section>
          </div>
        </article>
      </main>
    </div>
  );
};

export default TermsOfUse;